import useCurrentRoute from "@/hooks/useCurrentRoute";
import { isNoUOrEmptyString } from "@/lib/utils";
import { adminRoutes, publicRoutes } from "@/routes";
import type { AuthenticatedPageProps, UnauthenticatedPageProps } from "@/types";
import type { MatchDto } from "@/types/app/MatchDto";
import { Link as InertiaLink, usePage } from "@inertiajs/react";
import {
	Avatar,
	Button,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownTrigger,
	Image,
	Link,
	Navbar,
	NavbarBrand,
	NavbarContent,
	NavbarItem,
	NavbarMenu,
	NavbarMenuItem,
	NavbarMenuToggle,
} from "@nextui-org/react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import logo from "../assets/logo.png";
import { MatchDrawer } from "./MatchDrawer";
import { ModeToggle } from "./ModeToggle";

type NavigationProps = {
	type: "admin" | "public";
	isRegistrationOpen?: boolean;
};

export default function Navigation({
	type,
	isRegistrationOpen,
}: NavigationProps) {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const isAdmin = type === "admin";

	const { app } = usePage<AuthenticatedPageProps | UnauthenticatedPageProps>()
		.props;

	const { auth } = usePage<AuthenticatedPageProps>().props;
	const currentRoute = useCurrentRoute();

	const routes = isAdmin ? adminRoutes : publicRoutes;
	const currentRoutesOnRegistrationOpen = useMemo(
		() =>
			!isRegistrationOpen
				? routes.filter((route) => route.title !== "Registrace")
				: routes,
		[isRegistrationOpen, routes],
	);

	const results = useMemo(() => {
		return app.sharedData.matches
			.filter(
				(match) =>
					!isNoUOrEmptyString(match.score) && !isNoUOrEmptyString(match.date),
			)
			.sort((a: MatchDto, b: MatchDto) => {
				const dateA = new Date(a.date ?? 0).getTime();
				const dateB = new Date(b.date ?? 0).getTime();
				return dateB - dateA;
			})
			.slice(0, 5);
	}, [app.sharedData.matches]);

	const upcomingMatches = useMemo(() => {
		return app.sharedData.matches
			.filter(
				(match) =>
					isNoUOrEmptyString(match.score) && !isNoUOrEmptyString(match.date),
			)
			.slice(0, 5);
	}, [app.sharedData.matches]);

	return (
		<Navbar onMenuOpenChange={setIsMenuOpen} isBordered maxWidth="full">
			<NavbarContent>
				<NavbarMenuToggle
					aria-label={isMenuOpen ? "Close menu" : "Open menu"}
					className="sm:hidden"
				/>
				<NavbarBrand as="a" href="/">
					<Image src={logo} className="mr-3 h-6 sm:h-9" alt="Logo" />
					<p className="font-bold text-inherit">
						{app.prod ? "RTL" : <span className="text-danger">RTL - TEST</span>}
					</p>
				</NavbarBrand>
			</NavbarContent>

			<NavbarContent className="hidden sm:flex gap-4" justify="center">
				{currentRoutesOnRegistrationOpen.map((item, index) =>
					item.subItems && item.subItems.length > 0 ? (
						<Dropdown key={index}>
							<NavbarItem
								isActive={item.subItems.some(
									(subItem) => subItem.href === currentRoute,
								)}
							>
								<DropdownTrigger>
									<Button
										disableRipple
										className="p-0 bg-transparent data-[hover=true]:bg-transparent"
										endContent={<ChevronDown />}
										radius="sm"
										size="lg"
										variant="light"
									>
										{item.title}
									</Button>
								</DropdownTrigger>
							</NavbarItem>
							<DropdownMenu
								aria-label="Submenu"
								itemClasses={{
									base: "gap-4",
								}}
							>
								{item.subItems.map((subItem, subIndex) => (
									<DropdownItem key={`${subItem}-${subIndex}`}>
										<Link
											href={subItem.href}
											size="sm"
											as={subItem.isInertia ? InertiaLink : "a"}
											color="foreground"
										>
											{subItem.title}
										</Link>
									</DropdownItem>
								))}
							</DropdownMenu>
						</Dropdown>
					) : (
						<NavbarItem
							key={`${item}-${index}`}
							isActive={currentRoute === item.href}
						>
							<Link
								href={item.href}
								as={item.isInertia ? InertiaLink : "a"}
								color={currentRoute === item.href ? "primary" : "foreground"}
							>
								{item.title}
							</Link>
						</NavbarItem>
					),
				)}
			</NavbarContent>
			<NavbarContent as="div" justify="end">
				{isAdmin ? (
					<Dropdown placement="bottom-end">
						<DropdownTrigger>
							<Avatar
								isBordered
								as="button"
								className="transition-transform"
								color={app.prod ? "primary" : "warning"}
								name={auth?.user.nickname ?? auth?.user.name}
								size="sm"
								src={auth?.user.picture}
							/>
						</DropdownTrigger>
						<DropdownMenu aria-label="Profile Actions" variant="flat">
							<DropdownItem key="profile" className="h-14 gap-2">
								<p className="font-semibold">Přihlášený uživatel</p>
								<p className="font-semibold">
									{auth?.user.nickname ?? auth?.user.name}
								</p>
							</DropdownItem>
							<DropdownItem key="version">
								<Link
									href="/admin/changelog"
									color="foreground"
									as={InertiaLink}
								>
									Informace o verzi
								</Link>
							</DropdownItem>
							<DropdownItem key="logout">
								<Link href="/logout" color="danger">
									Odhlásit se
								</Link>
							</DropdownItem>
						</DropdownMenu>
					</Dropdown>
				) : (
					<div className="flex flex-row gap-1">
						<MatchDrawer matches={results ?? []} type="results" />
						<MatchDrawer matches={upcomingMatches ?? []} type="matches" />
					</div>
				)}
			</NavbarContent>
			<NavbarContent>
				<ModeToggle />
			</NavbarContent>
			<NavbarMenu>
				{currentRoutesOnRegistrationOpen.map((item, index) =>
					item.subItems && item.subItems.length > 0 ? (
						<Dropdown key={index}>
							<NavbarMenuItem
								isActive={item.subItems.some(
									(subItem) => subItem.href === currentRoute,
								)}
							>
								<DropdownTrigger>
									<Button
										disableRipple
										className="bg-transparent data-[hover=true]:bg-transparent pl-0"
										endContent={<ChevronRight />}
										size="lg"
										variant="light"
									>
										{item.title}
									</Button>
								</DropdownTrigger>
							</NavbarMenuItem>
							<DropdownMenu aria-label="Submenu">
								{item.subItems.map((subItem, subIndex) => (
									<DropdownItem key={`${subItem}-${subIndex}`}>
										<Link
											href={subItem.href}
											size="sm"
											as={subItem.isInertia ? InertiaLink : "a"}
											color="foreground"
										>
											{subItem.title}
										</Link>
									</DropdownItem>
								))}
							</DropdownMenu>
						</Dropdown>
					) : (
						<NavbarMenuItem
							key={`${item}-${index}`}
							isActive={currentRoute === item.href}
						>
							<Link
								href={item.href}
								as={item.isInertia ? InertiaLink : "a"}
								color={currentRoute === item.href ? "primary" : "foreground"}
							>
								{item.title}
							</Link>
						</NavbarMenuItem>
					),
				)}
			</NavbarMenu>
		</Navbar>
	);
}
